import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { UserPlus, Mail, Lock, User, Phone, Building, MapPin, Calendar, Building2, Eye, EyeOff } from 'lucide-react';
import AddressAutocomplete from '../components/AddressAutocomplete';

export default function RegisterPage() {
  const { register } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [formData, setFormData] = useState({
    company: '',
    name: '',
    firstname: '',
    birth_date: '',
    address: '',
    city: '',
    postal_code: '',
    phone: '',
    email: '',
    password: ''
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (formData.password.length < 6) {
      toast.error('Le mot de passe doit contenir au moins 6 caractères');
      return;
    }
    if (!formData.name || !formData.firstname) {
      toast.error('Le nom et prénom sont obligatoires');
      return;
    }
    if (!formData.phone) {
      toast.error('Le numéro de téléphone est obligatoire');
      return;
    }
    if (!formData.address || !formData.postal_code || !formData.city) {
      toast.error('L\u2019adresse complete (rue, code postal, ville) est obligatoire');
      return;
    }
    setLoading(true);
    try {
      await register(formData);
      toast.success('Compte créé avec succès !');
      navigate('/');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de l\'inscription');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#09090B] flex items-center justify-center py-12 px-4">
      <Card className="w-full max-w-2xl bg-zinc-900/50 border-zinc-800">
        <CardHeader className="text-center">
          <div className="w-16 h-16 bg-red-600/20 rounded-full flex items-center justify-center mx-auto mb-4">
            <UserPlus className="text-red-500" size={32} />
          </div>
          <CardTitle className="text-2xl text-white">Créer un compte</CardTitle>
          <CardDescription className="text-zinc-400">
            Rejoignez Le Hub Atelier et profitez de nos avantages
          </CardDescription>
          
          {/* Toggle between Particulier and Entreprise */}
          <div className="flex justify-center gap-2 mt-4">
            <Button className="bg-red-600 hover:bg-red-700">
              <User size={16} className="mr-2" />
              Particulier
            </Button>
            <Link to="/inscription-societe">
              <Button variant="outline" className="border-zinc-700 text-zinc-400 hover:bg-zinc-800">
                <Building2 size={16} className="mr-2" />
                Entreprise
              </Button>
            </Link>
          </div>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4">
            {/* Société (optionnel, B2B) */}
            <div>
              <Label className="text-zinc-300">Société <span className="text-zinc-500 text-xs">(optionnel - pour les professionnels)</span></Label>
              <div className="relative mt-2">
                <Building className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500" size={18} />
                <Input
                  placeholder="Nom de votre société (laisser vide si particulier)"
                  value={formData.company}
                  onChange={(e) => setFormData({...formData, company: e.target.value})}
                  className="bg-zinc-950 border-zinc-800 text-white pl-10"
                  data-testid="register-company"
                />
              </div>
            </div>

            {/* Nom et Prénom sur la même ligne */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label className="text-zinc-300">Nom <span className="text-red-500">*</span></Label>
                <div className="relative mt-2">
                  <User className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500" size={18} />
                  <Input
                    required
                    placeholder="Votre nom"
                    value={formData.name}
                    onChange={(e) => setFormData({...formData, name: e.target.value})}
                    className="bg-zinc-950 border-zinc-800 text-white pl-10"
                    data-testid="register-name"
                  />
                </div>
              </div>
              <div>
                <Label className="text-zinc-300">Prénom <span className="text-red-500">*</span></Label>
                <div className="relative mt-2">
                  <User className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500" size={18} />
                  <Input
                    required
                    placeholder="Votre prénom"
                    value={formData.firstname}
                    onChange={(e) => setFormData({...formData, firstname: e.target.value})}
                    className="bg-zinc-950 border-zinc-800 text-white pl-10"
                    data-testid="register-firstname"
                  />
                </div>
              </div>
            </div>

            {/* Date de naissance */}
            <div>
              <Label className="text-zinc-300">Date de naissance <span className="text-red-500">*</span></Label>
              <div className="relative mt-2">
                <Calendar className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500" size={18} />
                <Input
                  type="date"
                  value={formData.birth_date}
                  onChange={(e) => setFormData({...formData, birth_date: e.target.value})}
                  className="bg-zinc-950 border-zinc-800 text-white pl-10"
                  data-testid="register-birthdate"
                />
              </div>
            </div>

            {/* Adresse avec autocomplete BAN */}
            <div>
              <Label className="text-zinc-300">Adresse <span className="text-red-500">*</span> <span className="text-zinc-500 text-xs">(saisie assistee)</span></Label>
              <div className="mt-2">
                <AddressAutocomplete
                  value={formData.address}
                  onChange={(v) => setFormData((p) => ({ ...p, address: v }))}
                  onSelect={(s) => setFormData((p) => ({
                    ...p,
                    address: s.address || s.label,
                    postal_code: s.postcode || p.postal_code,
                    city: s.city || p.city,
                  }))}
                  required
                  placeholder="Ex : 142 boulevard Murat, 75016 Paris"
                  testid="register-address"
                />
              </div>
            </div>

            {/* Code postal & Ville sur la meme ligne */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label className="text-zinc-300">Code postal <span className="text-red-500">*</span></Label>
                <div className="relative mt-2">
                  <Input
                    required
                    placeholder="75000"
                    value={formData.postal_code}
                    onChange={(e) => setFormData({...formData, postal_code: e.target.value})}
                    className="bg-zinc-950 border-zinc-800 text-white"
                    data-testid="register-postalcode"
                  />
                </div>
              </div>
              <div>
                <Label className="text-zinc-300">Ville <span className="text-red-500">*</span></Label>
                <div className="relative mt-2">
                  <Input
                    required
                    placeholder="Votre ville"
                    value={formData.city}
                    onChange={(e) => setFormData({...formData, city: e.target.value})}
                    className="bg-zinc-950 border-zinc-800 text-white"
                    data-testid="register-city"
                  />
                </div>
              </div>
            </div>

            {/* Téléphone */}
            <div>
              <Label className="text-zinc-300">Numéro de téléphone <span className="text-red-500">*</span></Label>
              <div className="relative mt-2">
                <Phone className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500" size={18} />
                <Input
                  required
                  type="tel"
                  placeholder="06 12 34 56 78"
                  value={formData.phone}
                  onChange={(e) => setFormData({...formData, phone: e.target.value})}
                  className="bg-zinc-950 border-zinc-800 text-white pl-10"
                  data-testid="register-phone"
                />
              </div>
            </div>

            {/* Email */}
            <div>
              <Label className="text-zinc-300">Email <span className="text-red-500">*</span></Label>
              <div className="relative mt-2">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500" size={18} />
                <Input
                  required
                  type="email"
                  placeholder="votre@email.com"
                  value={formData.email}
                  onChange={(e) => setFormData({...formData, email: e.target.value})}
                  className="bg-zinc-950 border-zinc-800 text-white pl-10"
                  data-testid="register-email"
                />
              </div>
            </div>

            {/* Mot de passe */}
            <div>
              <Label className="text-zinc-300">Mot de passe <span className="text-red-500">*</span></Label>
              <div className="relative mt-2">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500" size={18} />
                <Input
                  required
                  type={showPassword ? "text" : "password"}
                  placeholder="Min. 6 caractères"
                  value={formData.password}
                  onChange={(e) => setFormData({...formData, password: e.target.value})}
                  className="bg-zinc-950 border-zinc-800 text-white pl-10 pr-10"
                  data-testid="register-password"
                />
                <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-500 hover:text-zinc-300" tabIndex={-1}>
                  {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
              </div>
            </div>

            <Button 
              type="submit" 
              className="w-full bg-red-600 hover:bg-red-700 h-12 mt-6"
              disabled={loading}
              data-testid="register-submit"
            >
              {loading ? 'Création...' : 'Créer mon compte'}
            </Button>
          </form>

          <div className="mt-6 text-center">
            <p className="text-zinc-500">
              Déjà un compte ?{' '}
              <Link to="/connexion" className="text-red-500 hover:underline">
                Se connecter
              </Link>
            </p>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
